import {LexicalComposer} from "@lexical/react/LexicalComposer";
import {$isModernSoundNode, ModernSoundNode} from "../ModernEditor/ModernSoundNode";
import {HeadingNode} from "@lexical/rich-text";
import Container from "../../components/Container";
import {RichTextPlugin} from "@lexical/react/LexicalRichTextPlugin";
import {ContentEditable} from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import {OnChangePlugin} from "@lexical/react/LexicalOnChangePlugin";
import {NodeEventPlugin} from "@lexical/react/LexicalNodeEventPlugin";
import {HistoryPlugin} from "@lexical/react/LexicalHistoryPlugin";
import {MarkdownShortcutPlugin} from "@lexical/react/LexicalMarkdownShortcutPlugin";
import {HEADING} from "@lexical/markdown";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";
import {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {RendleyStore} from "../../agenticEditor/store/RendleyStore";
import {ApplicationStore} from "../../agenticEditor/store/ApplicationStore";
import {BuildOnRoot, ClipsToTimelineItems, CreateMultiTrackEditorState} from "../ModernEditor/MultitrackTranscript";
import {reaction} from "mobx";
import {RendleyService} from "../../agenticEditor/services/RendleyService";
import {$getNodeByKey, $getRoot, $getSelection, $nodesOfType} from "lexical";
import {Engine} from "@rendley/sdk";
import AgenticEditCard from "./AgenticEditCard";

const editorInputStyle = {
    textAlign: 'justify',
    overflowY: 'auto', // Ensures content is scrollable only vertically
    height: 'calc(100vh - 200px)', // Optional for setting a fixed height
    maxWidth: '820px',
    paddingLeft: '50px',
    paddingRight: '50px',
    paddingTop: '20px',
    scrollbarWidth: 'none',
    outline: 'none',
};

function RefPlugin({editorRef}) {
    const [editor] = useLexicalComposerContext()
    editorRef.current = editor
    return null
}

const AgenticTextEditor = ({editorRef}) => {

    const [currentTime, setCurrentTime] = useState(RendleyStore.currentTime);
    const [selectedLibrettoClipId, setSelectedLibrettoClipId] = useState(ApplicationStore.selectedLibrettoClipId);
    const [scriptLayerId, setScriptLayerId] = useState(RendleyStore.scriptLayerId);

    useEffect(() => {
        const dispose = reaction(
            () => ({
                selectedLibrettoClipId: ApplicationStore.selectedLibrettoClipId,
                currentTime: RendleyStore.currentTime,
                scriptLayerId: RendleyStore.scriptLayerId,
            }),
            (state) => {
                setSelectedLibrettoClipId(state.selectedLibrettoClipId);
                setCurrentTime(state.currentTime);
                setScriptLayerId(state.scriptLayerId);
            }
        );
        return () => {
            dispose();
        };
    }, []);

    const timelineStateLayers = useMemo(() => {
        if (!scriptLayerId) return null;

        const scriptLayer = RendleyStore.layers[scriptLayerId];
        const librettoClipIds = RendleyStore.getLibrettoClipIdsForLayer(scriptLayer.id);

        const clipIds = librettoClipIds
            .flatMap(librettoClipId => RendleyStore.getClipIdsForLibrettoClip(librettoClipId))
            .filter(Boolean); // remove null/undefined ids

        const clips = clipIds
            .map(id => RendleyStore.clips[id])
            .filter(Boolean); // remove undefined clips

        return ClipsToTimelineItems(clips);
    }, [scriptLayerId, RendleyStore.layers, RendleyStore.clips]);

    const editorState = CreateMultiTrackEditorState({
        timelineStateLayers: timelineStateLayers
    });

    useEffect(() => {
        const handleClipUpdated = () => {
            if (!editorRef.current) return;

            const scriptLayerClipIds = RendleyStore.scriptLayerId
                ? RendleyStore.layers[RendleyStore.scriptLayerId].clipsIds
                : [];

            const scriptLayerClips = scriptLayerClipIds
                .map(id => RendleyStore.clips[id])
                .filter(clip => clip !== undefined);

            const timelineItems = ClipsToTimelineItems(scriptLayerClips);
            editorRef.current.recreateEditorState(timelineItems);
        };

        // Initial call to populate editor
        handleClipUpdated();

        // Listen to clip events
        window.addEventListener("LIBRETTO_CLIP_UPDATED", handleClipUpdated);
        window.addEventListener("LIBRETTO_CLIP_ADDED", handleClipUpdated);
        window.addEventListener("LIBRETTO_CLIP_REMOVED", handleClipUpdated);

        return () => {
            window.removeEventListener("LIBRETTO_CLIP_UPDATED", handleClipUpdated);
            window.removeEventListener("LIBRETTO_CLIP_ADDED", handleClipUpdated);
            window.removeEventListener("LIBRETTO_CLIP_REMOVED", handleClipUpdated);
        };
    }, [scriptLayerId]);

    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.updateCurrentTime(currentTime, 0);
        }
    }, [currentTime]);

    return (
        <AgenticTextEditorInner ref={editorRef}
                                editorState={editorState}/>
    )
}

const AgenticTextEditorInner = forwardRef(function AgenticTextEditorInner(
    {
        editorState
    },
    ref,
) {

    const editorRef = useRef(null);
    const [showEditCard, setShowEditCard] = useState(false);
    const [editCardPos, setEditCardPos] = useState({x: null, y: null});
    const [currentNodeElement, setCurrentNodeElement] = useState(null);
    const previousNodeElementRef = useRef(null);

    useEffect(() => {

        if (currentNodeElement) {
            currentNodeElement.style.backgroundColor = 'rgba(255, 0, 0, 0.5)';
        }

        if (previousNodeElementRef.current) {
            previousNodeElementRef.current.style.backgroundColor = 'transparent';
        }
        previousNodeElementRef.current = currentNodeElement;

    }, [currentNodeElement]);

    const initialConfig = {
        namespace: 'Libretto',
        theme: {
            ltr: 'ltr',
            rtl: 'rtl',
            placeholder: 'editor-placeholder',
            paragraph: 'editor-paragraph',
        },
        editable: true,
        onError: (err) => {
            console.error('Error:', err)
        },
        nodes: [ModernSoundNode, HeadingNode],
        editorState: editorState,
    };

    useImperativeHandle(
        ref,
        () =>
            ({
                recreateEditorState(timelineStateLayers, corrections) {
                    if (!editorRef.current) {
                        return;
                    }

                    editorRef.current?.update(() => {
                        const root = $getRoot()
                        root.clear();
                        BuildOnRoot({timelineStateLayers, corrections, root});
                    });
                },
                updateCurrentTime(time) {
                    editorRef.current?.update(() => {
                        const allNodes = getAllSoundNodes();
                        const node = allNodes.find((n) => n.getStart() <= time && n.getEnd() >= time);
                        if (node) {
                            const nodeKey = node.getKey();
                            if (!nodeKey) {
                                return;
                            }
                            const element = editorRef.current.getElementByKey(nodeKey);
                            if (element === null) {
                                return;
                            }
                            setCurrentNodeElement(element);
                            element.scrollIntoView({behavior: 'smooth', block: 'center'});
                        }
                    });
                },
                getAllSoundNodes,
            }),
        [],
    )

    const handleSelectionChange = (editorState, editor) => {
        editorState.read(() => {
            const selection = $getSelection();
            if (!selection || selection.isCollapsed()) {
                setShowEditCard(false);
                return;
            }
            const nodes = selection.getNodes();
            if (!nodes || nodes.length === 0) {
                setShowEditCard(false);
                return;
            }
            const firstKey = nodes[0].key || nodes[0].__key;
            const element = editor.getElementByKey(firstKey);
            if (!element) {
                setShowEditCard(false);
                return;
            }
            const rect = element.getBoundingClientRect();
            setEditCardPos({x: rect.left + window.scrollX, y: rect.top + window.scrollY});
            setShowEditCard(true);
        });
    };

    const focusEditor = () => {
        if (editorRef.current) {
            editorRef.current.focus();
        }
    };

    function getAllSoundNodes() {
        return (
            editorRef.current?.getEditorState().read(() => {
                return $nodesOfType(ModernSoundNode)
            }) ?? []
        )
    }


    const handleCopy = () => {
        focusEditor();
        setShowEditCard(false);
    };

    const handleCut = () => {
        focusEditor();
        setShowEditCard(false);
    };

    const handlePaste = async () => {
        focusEditor();
        const text = await navigator.clipboard.readText();
        setShowEditCard(false);
    };

    const handleSoundNodeClick = async (event, editor, nodeKey) => {
        const node = $getNodeByKey(nodeKey);
        const clickTime = ((node.getEnd() + node.getStart()) / 2);
        await RendleyService.seek(clickTime);
    }

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <Container
                bg="white"
                // my="1"
                py="0"
                px="8"
                fontSize="17px"
                fontWeight="normal"
                style={{
                    overflowY: 'auto',
                    margin: 0,
                    padding: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    backgroundColor: "white",
                    borderRadius: "12px"
                }}
            >
                <RichTextPlugin
                    contentEditable={
                        <ContentEditable style={editorInputStyle} spellCheck={false}/>
                    }
                    ErrorBoundary={LexicalErrorBoundary}
                    placeholder={""}/>
                <OnChangePlugin onChange={handleSelectionChange}/>
                <NodeEventPlugin nodeType={ModernSoundNode} eventType={'mouseover'}
                                 eventListener={() => {
                                 }}/>
                <NodeEventPlugin nodeType={ModernSoundNode} eventType={'mouseout'}
                                 eventListener={() => {
                                 }}/>
                <NodeEventPlugin nodeType={ModernSoundNode} eventType={'click'} eventListener={handleSoundNodeClick}/>
                <HistoryPlugin/>
                <RefPlugin editorRef={editorRef}/>
                <MarkdownShortcutPlugin transformers={[HEADING]}/>
                {showEditCard && <AgenticEditCard x={editCardPos.x}
                                                  y={editCardPos.y}
                                                  onCut={handleCut}
                                                  onCopy={handleCopy}
                                                  onPaste={handlePaste}/>}
            </Container>
        </LexicalComposer>
    )
});

export default AgenticTextEditor;